// Maps the bps-go permission catalog to the CP2 role-editor matrix. This curated
// surface is a DISPLAY filter only; parse/serialize round-trip every grant a role
// holds, so hidden ones survive a save.

// Feature catalog, grouped to mirror the sidebar. `permissions` are the catalog
// key(s) a feature's Read/Write toggles set; `supporting`/`supportingWrite` are
// extra permissions the page needs but has no toggle for, auto-bundled on save.
export const CP2_FEATURE_GROUPS = [
  {
    key: "card_issuing",
    label: "Card Issuing",
    features: [
      {
        id: "cardholders",
        label: "Cardholders",
        description:
          "View cardholder details and card information. Write allows creating and updating cardholders.",
        permissions: ["cardholders"],
        // List endpoint needs card_issuing.read; bulk-create needs .write.
        supporting: ["card_issuing.read"],
        supportingWrite: ["card_issuing.write"],
      },
      {
        id: "programs",
        label: "Programs",
        description: "View the company's card programs and their configuration.",
        permissions: ["programs"],
        supporting: ["card_issuing.read"],
      },
      {
        id: "card_orders",
        label: "Card Orders",
        description:
          "View card orders. Write allows placing and managing new orders.",
        permissions: ["card_orders"],
        supporting: [
          "card_issuing.read",
          "programs.read",
          "offices.offices.read",
          "offices.shipping_addresses.read",
        ],
      },
      {
        id: "sell_orders",
        label: "Sell Orders",
        description:
          "View and manage sell orders, including Search by Proxy, Combine, the Shift and Kiosk reports, and voiding an entire order.",
        permissions: ["sell_orders"],
        supporting: [
          "offices.offices.read",
          "corporate_customers.read",
          "cardholders.read",
          "programs.read",
          "sell_order.check_card_balance.write",
          "sell_order.void_card.read",
          // PIB-251 AC9: sell order access carries specialty items with it, so
          // no Oxford role has to be edited for an agent to sell them.
          "sell_specialty_items.read",
        ],
        // Voiding a whole order folds into Sell Orders write (no separate row).
        supportingWrite: ["sell_order.void_card.write"],
      },
      {
        id: "batch_sell_orders",
        label: "Batch Sell Orders",
        description:
          "View batch sell orders. Write allows creating new batch orders.",
        permissions: ["sell_order.batch_sell_order"],
        supporting: [
          "sell_orders.read",
          "offices.offices.read",
          "corporate_customers.read",
          "cardholders.read",
          "programs.read",
          "sell_order.check_card_balance.write",
        ],
      },
      {
        id: "replace_cards",
        label: "Replace Cards",
        description:
          "View card-replacement orders. Write allows creating replacements.",
        permissions: ["sell_order.replace_card"],
        supporting: [
          "sell_orders.read",
          "offices.offices.read",
          "cardholders.read",
          "programs.read",
          "sell_order.check_card_balance.write",
        ],
      },
      {
        id: "check_card_balance",
        label: "Check Card Balance",
        description: "Look up a card's balance by proxy number.",
        permissions: ["sell_order.check_card_balance"],
        supporting: ["sell_orders.read", "cardholders.read"],
      },
      {
        id: "void_cards",
        label: "Void Cards",
        description:
          "View voided cards. Write allows voiding individual cards from a sell order.",
        permissions: ["sell_orders_items"],
        supporting: ["sell_orders.read"],
      },
    ],
  },
  {
    key: "offices",
    label: "Offices",
    features: [
      {
        id: "offices",
        label: "Offices",
        description:
          "View the company's offices. Write allows creating and editing offices.",
        permissions: ["offices.offices"],
      },
      {
        id: "shipping_addresses",
        label: "Shipping Addresses",
        description:
          "View shipping addresses. Write allows creating and editing them.",
        permissions: ["offices.shipping_addresses"],
        supporting: ["offices.offices.read"],
      },
    ],
  },
  {
    key: "reports",
    label: "Reports",
    features: [
      {
        id: "company_reporting",
        label: "Company Reports",
        description:
          "View company reports. Write allows creating new reports.",
        permissions: ["company_reporting"],
      },
      {
        id: "reporting",
        label: "Program Report Subscriptions",
        description:
          "View program report subscriptions. Write allows creating and editing scheduled reports.",
        permissions: ["reports.reports"],
        supporting: ["card_issuing.read"],
      },
    ],
  },
  {
    key: "settings",
    label: "Settings",
    features: [
      {
        id: "users",
        label: "Users",
        description:
          "View users in the company. Write allows inviting, editing, and disabling users.",
        permissions: ["settings.users"],
        supporting: ["settings.user_roles.read", "offices.offices.read"],
      },
      {
        id: "user_roles",
        label: "User Roles",
        description:
          "View the company's roles. Write allows creating, editing, and deleting roles.",
        warning:
          "Write lets this role change what every user in the company can access.",
        permissions: ["settings.user_roles"],
      },
      {
        id: "unlock_users",
        label: "Unlock Users",
        description: "Allows unlocking users who are locked out of their account.",
        permissions: ["users_unlock"],
        supporting: ["settings.users.read"],
      },
      {
        id: "corporate_customers",
        label: "Corporate Customers",
        description:
          "View corporate customers. Write allows creating and editing them.",
        permissions: ["corporate_customers"],
      },
      {
        id: "sell_specialty_items",
        label: "Specialty Items",
        description:
          "View the company's specialty items. Write allows adding and editing them, including their price.",
        permissions: ["sell_specialty_items"],
      },
      {
        id: "audit_logs",
        label: "Audit Logs",
        description: "View the account activity and audit log.",
        permissions: ["audit_logs"],
      },
    ],
  },
];

// Friendly labels for supporting (auto-bundled) permissions, shown in the matrix
// as the "Also grants" line.
const SUPPORTING_LABELS = {
  "card_issuing.read": "Card issuing (view)",
  "card_issuing.write": "Card issuing (manage)",
  "programs.read": "Program details",
  "offices.offices.read": "Offices (view)",
  "offices.shipping_addresses.read": "Shipping addresses (view)",
  "corporate_customers.read": "Corporate customers (view)",
  "cardholders.read": "Cardholders (view)",
  "sell_orders.read": "Sell orders (view)",
  "sell_order.check_card_balance.write": "Card balance validation",
  "sell_order.void_card.read": "Void order (view)",
  "sell_order.void_card.write": "Void an entire order",
  "settings.user_roles.read": "User roles (view)",
  "settings.users.read": "Users (view)",
  "sell_specialty_items.read": "Specialty items (view)",
};
const supportingLabel = (name) => SUPPORTING_LABELS[name] || name;

// Office-scope marker (not a feature): flags a role company-wide for
// RBACService.office_scope; surfaced as a form toggle, not a matrix row.
export const OFFICE_ACCESS_KEY = "all_offices";

// Builds matrix display groups by intersecting the curated catalog with what the
// backend actually offers. Display-only; does not affect which grants persist.
export const buildPermissionGroups = (catalog) => {
  const cat = catalog || {};
  const groups = [];

  CP2_FEATURE_GROUPS.forEach((group) => {
    const features = [];

    group.features.forEach((feature) => {
      const keys = feature.permissions.filter((key) => cat[key]);
      if (keys.length === 0) return; // not seeded in this environment

      features.push({
        id: feature.id,
        name: feature.label,
        description: feature.description,
        warning: feature.warning,
        keys,
        availableRead: keys.some((key) => !!cat[key].read),
        availableWrite: keys.some((key) => !!cat[key].write),
        // Deduped friendly labels for the "Also grants" tooltip.
        supporting: [
          ...new Set(
            [...(feature.supporting || []), ...(feature.supportingWrite || [])].map(
              supportingLabel
            )
          ),
        ],
      });
    });

    if (features.length > 0) {
      groups.push({ key: group.key, label: group.label, features });
    }
  });

  return groups;
};

// Parses a role's permissions (response encoding) into matrix state keyed by
// resource -> { read, write }. Keeps EVERY key, even ones not displayed, so
// hidden grants round-trip on save.
export const parseRolePermissions = (rolePermissions) => {
  const selected = {};

  Object.entries(rolePermissions || {}).forEach(([key, levels]) => {
    selected[key] = {
      read: !!levels.read,
      write: !!levels.write,
    };
  });

  return selected;
};

// Every key backing a visible feature: the authoritative CP2-grantable set the
// editor shows, so serialization must only ever emit these.
const FEATURE_PERMISSION_KEYS = new Set(
  CP2_FEATURE_GROUPS.flatMap((group) =>
    group.features.flatMap((feature) => feature.permissions)
  )
);

// Out-of-matrix grants to preserve on save. Empty now that settings.user_roles
// is a first-class "User Roles" row.
const PRESERVED_KEYS = new Set();

// Keys the editor may persist: curated feature keys, the office-scope flag, and
// the preserved out-of-matrix grants above.
const GRANTABLE_KEYS = new Set([
  ...FEATURE_PERMISSION_KEYS,
  OFFICE_ACCESS_KEY,
  ...PRESERVED_KEYS,
]);

// Serializes matrix state to the flat ["resource.read", ...] request encoding.
// Emits ONLY grantable keys (+ bundled supporting) and drops the rest: bps-go
// rejects the whole PUT (422) on any unrecognized name.
export const serializePermissions = (selected) => {
  const result = new Set();

  Object.entries(selected || {}).forEach(([key, cell]) => {
    if (!GRANTABLE_KEYS.has(key)) return;
    if (cell?.read) result.add(`${key}.read`);
    if (cell?.write) result.add(`${key}.write`);
  });

  // Bundle each granted feature's supporting permissions so the role works.
  CP2_FEATURE_GROUPS.forEach((group) =>
    group.features.forEach((feature) => {
      const read = feature.permissions.some((key) => selected?.[key]?.read);
      const write = feature.permissions.some((key) => selected?.[key]?.write);
      // supporting: needed to use the page at all (bundled on any grant).
      if (read || write) (feature.supporting || []).forEach((n) => result.add(n));
      // supportingWrite: backend deps of write-only actions (e.g. bulk-create
      // needs card_issuing.write); bundled only when write is granted.
      if (write) (feature.supportingWrite || []).forEach((n) => result.add(n));
    })
  );

  return [...result];
};

// Maps each force-enabled supporting permission to the granted features that
// require it (e.g. "sell_orders.read" -> ["Check Card Balance"]), so the matrix
// can lock that checkbox while a dependent feature is granted.
export const computeForcedPermissions = (selected) => {
  const forced = {};
  const addFor = (name, label) => {
    if (!forced[name]) forced[name] = [];
    if (!forced[name].includes(label)) forced[name].push(label);
  };
  CP2_FEATURE_GROUPS.forEach((group) =>
    group.features.forEach((feature) => {
      const read = feature.permissions.some((key) => selected?.[key]?.read);
      const write = feature.permissions.some((key) => selected?.[key]?.write);
      if (read || write)
        (feature.supporting || []).forEach((n) => addFor(n, feature.label));
      if (write)
        (feature.supportingWrite || []).forEach((n) => addFor(n, feature.label));
    })
  );
  return forced;
};

// Reserved so a custom role can't collide with the Users editor's
// "role === role_name" legacy check. Matched case/separator-insensitively.
export const RESERVED_ROLE_NAMES = [
  "owner",
  "admin",
  "client_admin",
  "office_admin",
  "agent",
  "user",
];
export const isReservedRoleName = (name) => {
  const normalized = (name || "").trim().toLowerCase().replace(/\s+/g, "_");
  return RESERVED_ROLE_NAMES.includes(normalized);
};

// True when the selection grants at least one feature (read or write); used to
// block saving a role that grants nothing usable. The office-scope flag and
// hidden/internal grants do not count.
export const hasFeaturePermission = (selected) =>
  Object.entries(selected || {}).some(
    ([key, cell]) =>
      FEATURE_PERMISSION_KEYS.has(key) && (cell?.read || cell?.write)
  );
